import React, { useRef, useEffect } from 'react'
import Box from '@mui/material/Box'
import { IsThereAvatarBanner } from '@/components/IsThereAvatarBanner'
import {
  // EmtBreadcrumbs,
  EmtButton,
  // EmtHeader,
  EmtIcon,
  EmtSeparator,
  EmtTypography,
  useMobile,
} from 'emt-components-v2'
// import { Images, Routes } from '@/assets/configs'
// import NextLink from 'next/link'
import Skeleton from '@mui/material/Skeleton'
import {
  MobileHeader,
  ProfileContainer,
  ProfileMap,
  UserImagesList,
  UserImagesListLoading,
  Wrapper,
} from '@/components/Profile/partials'
import { LightBox } from '../Profile/partials/StyledComponents'
import {
  Availabilities,
  Benefices,
  Experience,
  List,
  Love,
  Similars,
  UserDetails,
} from '@/components/PetSitter/Profile'
import { InfoBox } from '@/components/PetSitter/InfoBox'
import { Info } from '@/components/PetSitter/Info'
import Container from '@mui/material/Container'
import Comments from '@/components/Profile/containers/Comments'
import { ImagesSliderModal } from '@/components/Profile/Modals'
import Footer from '@/components/EMT/Footer'
import MobileSwipeMenu from '@/components/PetSitter/MobileSwipeMenu'
import { MobileSwipeMenuRef } from '@/components/PetSitter/MobileSwipeMenu'
import { formatPrice } from '@/utils/formatPrice'
import { getPetsNumber } from '@/pages/pet-sitter/search'

// TYPES
import { Coordinate } from '@/data'
import { useRouter } from 'next/router'
import { messageSanitize } from '@/helpers/messageSanitize'
import AskSitterCard from '../PetSitter/AskSitterCard'
import VariantImages from '@/assets/BaseVariantImages'
import Calendar from '@/components/pages/pet-sitter/pro/my-services/calendar/Calendar'
import PetsittersSelection from '@/components/PetSitter/PetsittersSelection/PetsittersSelection'
import PetsittersSelectionButtons from '@/components/PetSitter/PetsittersSelection/PetsittersSelectionButtons'
import { useNewRequestCartStore } from '@/store/newRequestCartStore'
import { cancellationPoliciesMapping } from '@/data/PetSitter/Messages'

// Hooks
import { useTranslation } from 'next-i18next'
import useCurrentUser from '@/hooks/useCurrentUser'
import { usePetsitterProfileData } from '@/hooks/usePetsitterProfileData'
import { useModalScrollLock } from '@/hooks/useModalScrollLock'
import { useImageSlider } from '@/hooks/useImageSlider'
import useRNWebViewTranslation from '@/hooks/useRNWebViewTranslation'
import { useNotifications } from '@/modules/Notifications'

// Data
import { PRODUCTS_MAP } from '@/hooks/usePetsitterPrice'
import Constants from '@/assets/Constants'
import { gtmSendCustomEvent } from '@/helpers/GTMHelper'
import useFloatingProfile, { ProfileKind } from '@/hooks/useFloatingProfile'
import Routes from '@/assets/configs/Routes'
import useIsQueryOwner from '@/hooks/useIsQueryOwner'
import { PriceIncludedTooltip } from '@/components/PetSitter/PriceIncludedTooltip'

type Props = React.PropsWithChildren<{
  customerId: string
  overrideBackButton?: () => void
  getUserProfileName?: (name?: string) => void
  profile?: any
  scrollContainerRef?: React.RefObject<HTMLDivElement | null>
}>

const PetSitterProfileComponent: React.FC<Props> = ({
  customerId,
  overrideBackButton,
  getUserProfileName,
  profile,
  scrollContainerRef,
}) => {
  const { t } = useTranslation('profile')
  const { t: tc } = useTranslation('common')
  const hasRNWebView = useRNWebViewTranslation()
  const [, , appendQueryType] = useIsQueryOwner()
  const { currentUser } = useCurrentUser()
  const [addNotificationError] = useNotifications()

  useEffect(() => {
    router.replace(
      {
        pathname: router.pathname,
        query: {
          ...router.query,
          pview: 'true',
          type: 'dogowner',
        },
      },
      undefined,
      { shallow: true },
    )
  }, [])

  // [HOOK] Floating Mobile Profile
  const floating = useFloatingProfile({
    defaultProfileKind: ProfileKind.PetSitting,
    publicProfile: !currentUser,
  })

  const swipeMenuRef = useRef<MobileSwipeMenuRef>(null)

  const scrollWithOffset = (target: string | HTMLElement | null) => {
    const element = typeof target === 'string' ? document.getElementById(target) : target
    if (!element) return

    const scrollContainer = scrollContainerRef?.current

    if (scrollContainer) {
      const elementRect = element.getBoundingClientRect()
      const containerRect = scrollContainer.getBoundingClientRect()

      const top =
        scrollContainer.scrollTop +
        (elementRect.top - containerRect.top) -
        (isMobile && petsittersSelectionRef.current
          ? petsittersSelectionRef.current.getBoundingClientRect().height + 70
          : 70)

      scrollContainer.scrollTo({
        top: Math.max(0, top),
        behavior: 'smooth',
      })
      return
    }

    const rect = element.getBoundingClientRect()
    const absoluteTop = window.scrollY + rect.top

    window.scrollTo({
      top: Math.max(0, absoluteTop - window.innerHeight / 6),
      behavior: 'smooth',
    })
  }

  const [isMobile] = useMobile()

  const petsittersIds = useNewRequestCartStore((s) => s.petsittersIds)
  const petsToComplete = useNewRequestCartStore((s) => s.petsToComplete)

  const { activeModalSlider, setActiveModalSlider, imageClickedIndex, handleImageClick } = useImageSlider()
  const petsittersSelectionRef = useRef<HTMLDivElement>(null)
  const router = useRouter()

  // Send gtm event when see profile as connected user
  const hasSentProfileEvent = useRef(false)
  useEffect(() => {
    if (!currentUser || hasSentProfileEvent.current) return
    hasSentProfileEvent.current = true
    gtmSendCustomEvent('member_view_profile_petsitter_sst', {
      member_role_variable: 'maitre',
      member_id_variable: currentUser?.id || 'unknown',
      brand_variable: Constants.storageID,
      website_variable: Constants.rootUrl,
    })
  }, [currentUser])

  useModalScrollLock(activeModalSlider)

  // If no currentUser, set 'type' query param to 'dogowner' in the URL
  useEffect(() => {
    if (!currentUser && typeof window !== 'undefined') {
      const url = new URL(window.location.href)
      if (url.searchParams.get('type') !== 'dogowner') {
        url.searchParams.set('type', 'dogowner')
        window.history.replaceState({}, '', url.toString())
      }
    }
  }, [currentUser])

  const disponibilitiesRef = React.useRef<HTMLDivElement>(null)

  const {
    userProfile,
    isLoading,
    reviewsSummary,
    hasMoreReviews,
    reviewsLoadingMore,
    isInitialReviewsLoading,
    loadMoreReviews,
    petsitterPrice,
    reduction,
    quantity,
    allImages,
    statsData,
  } = usePetsitterProfileData({
    customerId,
    profile,
  })

  const backOrRedirectHome = () => {
    if (typeof window !== 'undefined' && window.history.length > 1) {
      router.back()
      return
    }

    router.push(appendQueryType(Routes.Home))
  }

  useEffect(() => {
    if (isLoading || userProfile) return

    if (Constants.node_env === 'production') {
      backOrRedirectHome()
    } else {
      addNotificationError({ error: new Error('Profile not found') })
      overrideBackButton?.()
    }
  }, [isLoading, userProfile, router, overrideBackButton])

  // Scroll to hash
  useEffect(() => {
    if (!router.isReady) return
    if (!userProfile) return

    if (router.asPath.includes('#reviews')) {
      requestAnimationFrame(() => {
        scrollWithOffset('reviews')
      })
    }
  }, [router.isReady, router.asPath, userProfile])

  return (
    <>
      {/* DESKTOP Header */}
      {/* {!isMobile && !!currentUser && (
        <EmtHeader backgroundColor="#762D8A" background={Images.Header.Pattern}>
          <IsThereAvatarBanner t={tc} showHeader={false} sx={{ paddingBottom: '30px' }} />
          {!isLoading && userProfile ? (
            <EmtBreadcrumbs variant="white">
              <NextLink href={Routes.Home} passHref legacyBehavior>
                <Box sx={{ display: 'flex', gap: '5px', cursor: 'pointer' }}>
                  <EmtIcon className="emt-paw" />
                  {tc('breadcrumbs.home')}
                </Box>
              </NextLink>
              <NextLink
                href={tendersEnabled ? Routes.PetSitter.Reservations.NewRequest : Routes.PetSitter.Base}
                passHref
                legacyBehavior
              >
                PetSitter
              </NextLink>
              <EmtTypography>{userProfile.name}</EmtTypography>
            </EmtBreadcrumbs>
          ) : (
            <Skeleton variant="text" height={24} width="40%" />
          )}
        </EmtHeader>
      )} */}
      {!isMobile && <EmtSeparator sx={{ height: '2px', backgroundColor: '#762D8A' }} />}
      {/* END DESKTOP Header */}
      {/* MOBILE Header */}
      {!isLoading && isMobile && userProfile ? (
        <>
          <IsThereAvatarBanner t={tc} sx={{ paddingTop: '80px' }} />
          <MobileHeader
            picturesUrls={allImages}
            handleImageClick={handleImageClick}
            overrideBackButton={overrideBackButton}
            color="#AE59C6"
            userIsNew={!!userProfile?.isNew}
            userLastRefresh={userProfile?.lastRefresh}
            reviewsCount={reviewsSummary?.reviewsCount}
            rating={reviewsSummary?.averageRating}
            averageResponseTimeUnderThreeHours={statsData?.averageResponseTimeUnderThreeHours}
            // pro
          />
        </>
      ) : isMobile ? (
        <Skeleton variant="rectangular" height={320} width="100%" />
      ) : null}
      {/* END MOBILE Header */}
      {/* CONTENT */}
      <Wrapper>
        <Container
          sx={{
            paddingTop: '30px',
            display: isMobile ? 'flex' : 'grid',
            gridTemplateColumns: '1fr 360px',
            alignItems: 'stretch',
            gap: '20px',
          }}
          maxWidth="lg"
        >
          {/* Profile Container */}
          <ProfileContainer>
            {!isLoading && userProfile ? (
              <>
                {/* DESKTOP Images List */}
                {!isMobile && (
                  <UserImagesList
                    picturesUrls={allImages}
                    userIsNew={!!userProfile?.isNew}
                    userLastRefresh={userProfile?.lastRefresh}
                    onClick={(imageIndex) => handleImageClick(imageIndex)}
                    reviewsCount={reviewsSummary?.reviewsCount}
                    rating={reviewsSummary?.averageRating}
                    distance={userProfile?.distance}
                    averageResponseTimeUnderThreeHours={statsData?.averageResponseTimeUnderThreeHours}
                  />
                )}
                {/* END DESKTOP Images list */}
                {/* User Details */}
                <UserDetails
                  firstName={userProfile.name}
                  distance={userProfile.distance}
                  reviewsToCount={reviewsSummary.reviewsCount}
                  averageReviewToRating={reviewsSummary.averageRating}
                  badges={userProfile.badges}
                  plainBadges={userProfile.plainBadges}
                  city={userProfile.city}
                  zipCode={userProfile.zipCode}
                  petsitterTitle={userProfile.title}
                  petsitter
                  onScrollToSection={scrollWithOffset}
                  statsData={statsData}
                  isReviewsLoading={isInitialReviewsLoading}
                />
                {/* END User Details */}

                {/* Petsitters selection */}
                {isMobile && (
                  <>
                    <PetsittersSelection
                      marginLeftTitle="38px"
                      ref={petsittersSelectionRef}
                      openFloatingProfile={floating.handleOpenProfile}
                    />
                  </>
                )}
                {/* END Petsitters selection */}
                {/* Experience */}
                <Box id="knowledge-section">
                  <Experience petsitterExperience={userProfile.petsitterExperience} />
                </Box>
                {/* END Experience */}
                {/* Availabilities */}
                <EmtTypography variant="h4" fontWeight={800}>
                  {t('petsitter-profile.isProposing', { name: userProfile.name })}
                </EmtTypography>
                <Availabilities data={userProfile.petsitterService} />
                {/* END Availabilities */}
                {/* Info */}
                <InfoBox
                  iconClass="emt-birthday"
                  title={userProfile.petsitterService?.minAnimalAgeSupport?.title}
                  text={userProfile.petsitterService?.minAnimalAgeSupport?.description}
                />
                {/* END Info */}
                {/* Criteria List */}
                {userProfile.petsitterService?.criteria && <List data={userProfile.petsitterService.criteria} />}
                {/* END Criteria List */}
                {/* Love */}
                <Box id="psychology-section">
                  <Love petsitterPetLoving={userProfile.petsitterPetLoving} name={userProfile.name} />
                </Box>
                {/* END Love */}
                {/* Needs to know */}
                <LightBox sx={{ minHeight: 'unset', padding: '27px', borderColor: '#E8E8EA' }}>
                  <EmtTypography variant="body1" fontWeight="700" fontSize="18px">
                    {t('petsitter-profile.needToKnow', { name: userProfile.name })}
                  </EmtTypography>
                  <EmtTypography variant="body1">
                    {messageSanitize(userProfile.petsitterService.petownerInformationsRequest, {
                      address: false,
                      keywords: false,
                    })}
                  </EmtTypography>
                </LightBox>
                {/* END Needs to know */}
                <EmtSeparator />
                {/* News photos */}
                <Info
                  image={VariantImages.CheckGreen}
                  title={userProfile.petsitterService.takeInformatedPetownerEngagement.title}
                  text={userProfile.petsitterService.takeInformatedPetownerEngagement.description}
                />
                {/* END News photos */}
                <EmtSeparator />
                {/* Cancellation Policy */}
                <InfoBox
                  iconClass="emt-calendar-no-access-1"
                  title={userProfile.petsitterService.cancellationPolicies?.title}
                  text={userProfile.petsitterService.cancellationPolicies?.description}
                />
                {/* END Cancellation Policy */}
                {/* Disponibilities */}
                <div ref={disponibilitiesRef}>
                  <EmtTypography variant="h4" fontWeight={800}>
                    {t('petsitter-profile.disponibilities', { name: userProfile.name })}
                  </EmtTypography>
                  <Calendar id={userProfile.uuid} viewOnly />
                </div>
                {/* END Disponibilities */}

                {/* Location */}
                <EmtTypography variant="h4" fontWeight={800}>
                  {t('petsitter-profile.location', { name: userProfile.name })}
                </EmtTypography>
                {userProfile?.profileHome ? (
                  <>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: isMobile ? '15px' : '38px' }}>
                      {userProfile?.profileHome?.homeType && (
                        <InfoBox
                          center
                          iconClass={userProfile.profileHome?.homeType.icon.type}
                          title={userProfile.profileHome?.homeType.title}
                          text={userProfile.profileHome?.homeType.description}
                        />
                      )}
                      {userProfile?.profileHome?.homeAreaType && (
                        <InfoBox
                          center
                          iconClass={userProfile.profileHome?.homeAreaType.icon.type}
                          title={userProfile.profileHome?.homeAreaType.title}
                          text={userProfile.profileHome?.homeAreaType.description}
                        />
                      )}
                    </Box>
                    {userProfile?.profileHome?.homeDescription && (
                      <EmtTypography variant="body1">
                        {messageSanitize(userProfile.profileHome?.homeDescription, { address: false, keywords: false })}
                      </EmtTypography>
                    )}
                    <EmtSeparator />
                    <Box sx={{ display: 'flex', gap: '20px' }}>
                      {userProfile.profileHome?.haveNatureProximity && (
                        <Info image={VariantImages.Plant} title={userProfile.profileHome?.haveNatureProximity.title} />
                      )}
                      {userProfile.profileHome?.isTabagist && (
                        <>
                          <EmtSeparator sx={{ height: '100%', width: '1px' }} />
                          <Info image={VariantImages.Nosmoke} title={userProfile.profileHome?.isTabagist.title} />
                        </>
                      )}
                    </Box>
                    {userProfile.profileHome?.homeInformations && (
                      <List data={userProfile.profileHome?.homeInformations} />
                    )}
                  </>
                ) : (
                  <EmtTypography variant="body1">
                    {userProfile.name} ne propose pas de garde à son domicile.
                  </EmtTypography>
                )}
                {/* END Location */}

                {/* Position */}
                <Box id="map-section">
                  <ProfileMap
                    address={
                      {
                        latitude: userProfile?.latitude,
                        longitude: userProfile?.longitude,
                      } as Coordinate
                    }
                    color="purple"
                  />
                </Box>
                {/* END Position */}
                {/* Languages List */}
                {userProfile.languages && <List data={userProfile.languages} />}
                {/* END Languages List */}
                {/* Comments */}
                <Box id="reviews">
                  {userProfile && (
                    <Comments
                      profile={{ ...userProfile, reviewsSummary, id: userProfile.uuid, displayName: userProfile.name }}
                      buttonColor="purple"
                      activeForm={true}
                      hasMoreReviews={hasMoreReviews}
                      reviewsLoadingMore={reviewsLoadingMore}
                      onLoadMoreReviews={loadMoreReviews}
                    />
                  )}
                </Box>
                {/* END Comments */}
              </>
            ) : (
              // Loading state
              <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: '30px' }}>
                {!isMobile && <UserImagesListLoading />}
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Skeleton variant="text" width="100%" height={58} />
                  <Skeleton variant="rectangular" width="100%" height={12} />
                </Box>
                <Box sx={{ display: 'flex', gap: '25px' }}>
                  <Skeleton variant="rectangular" height={120} width="100%" sx={{ borderRadius: '18px' }} />
                  <Skeleton variant="rectangular" height={120} width="100%" sx={{ borderRadius: '18px' }} />
                  <Skeleton variant="rectangular" height={120} width="100%" sx={{ borderRadius: '18px' }} />
                </Box>
                <Box>
                  <Skeleton variant="text" width="100%" height={20} />
                  <Skeleton variant="text" width="90%" height={20} />
                  <Skeleton variant="text" width="85%" height={20} />
                </Box>
                <Box>
                  <Skeleton variant="rectangular" width="100%" height={260} />
                </Box>
              </Box>
            )}
          </ProfileContainer>
          {/* END Profile Container */}

          {/* Profile Sidebar */}
          {!isMobile && (
            <Box
              sx={{
                minWidth: 0,
                position: 'relative',
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  position: 'sticky',
                  top: '20px',
                  // maxHeight: `calc(100vh - 120px)`,
                  display: 'flex',
                }}
              >
                {!isLoading && userProfile && (
                  <AskSitterCard
                    data={{
                      ...userProfile,
                      reviewsCount: reviewsSummary.reviewsCount,
                      reviewsRating: reviewsSummary.averageRating,
                    }}
                    onScrollToSection={scrollWithOffset}
                  />
                )}
              </Box>
              {/* END Profile Sidebar */}
            </Box>
          )}
        </Container>
        {!isMobile && (
          <PetsittersSelection
            ref={petsittersSelectionRef}
            sx={{ position: 'sticky', display: petsittersIds.length === 0 ? 'none' : 'block' }}
          />
        )}

        <Container maxWidth="lg" sx={{ paddingBottom: '54px' }}>
          <EmtSeparator sx={{ margin: '40px 0' }} />
          {/* Benefices */}
          <Benefices />
          {/* END Benefices */}
        </Container>
      </Wrapper>
      {/* END CONTENT */}
      {/* MODAL Carousel */}
      {!isLoading && (
        <ImagesSliderModal
          picturesUrls={allImages}
          initialSlide={imageClickedIndex}
          open={activeModalSlider}
          onClose={() => setActiveModalSlider(false)}
        />
      )}
      {/* END MODAL Carousel */}
      {/* Mobile ask sitter */}
      {isMobile && (
        <MobileSwipeMenu
          ref={swipeMenuRef}
          component={
            petsitterPrice ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  padding: hasRNWebView ? '35px 20px 0px' : '35px 20px 10px',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '12px',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: '12px',
                    }}
                  >
                    <Box>
                      <EmtTypography variant="body1" sx={{ fontWeight: '700', fontSize: '18px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <Box
                            sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                            onClick={() => swipeMenuRef.current?.open()}
                          >
                            {reduction && (
                              <EmtTypography variant="body2" sx={{ textDecoration: 'line-through' }}>
                                {formatPrice(petsitterPrice.total + reduction)}€
                              </EmtTypography>
                            )}
                            <EmtTypography
                              variant="body2"
                              fontWeight={700}
                              textAlign="center"
                              fontSize="18px"
                              sx={{ color: reduction ? '#18BA92' : 'inherit' }}
                            >
                              {formatPrice(petsitterPrice.total)}€
                            </EmtTypography>
                          </Box>
                          <Box
                            sx={{ display: 'flex', alignItems: 'center' }}
                            onClick={(e) => {
                              e.preventDefault
                              e.stopPropagation()
                            }}
                          >
                            <PriceIncludedTooltip />
                          </Box>
                        </Box>
                      </EmtTypography>
                      <EmtTypography variant="body2" color="#77757F" onClick={() => swipeMenuRef.current?.open()}>
                        {PRODUCTS_MAP[petsitterPrice?.orderDetails?.[0]?.productSlug]?.completeTitle},{' '}
                        {Math.max(1, quantity)} {PRODUCTS_MAP[petsitterPrice?.orderDetails?.[0]?.productSlug]?.title}
                        {Math.max(1, quantity) > 1 ? 's' : ''},{' '}
                        <span style={{ whiteSpace: 'nowrap' }}>
                          {` ${Math.max(getPetsNumber(petsToComplete), 1)} anima${
                            getPetsNumber(petsToComplete) > 1 ? 'ux' : 'l'
                          }`}
                        </span>
                      </EmtTypography>
                    </Box>
                  </Box>
                  <EmtButton
                    variant="outlined"
                    color="purple"
                    size="small"
                    className="!w-[34px] !h-[34px] !p-0 flex-shrink-0 !min-w-0"
                    sx={{ borderColor: '#E8E8EA' }}
                    onClick={() => swipeMenuRef.current?.open()}
                  >
                    <EmtIcon className="emt-edit" size="small" sx={{ fontSize: '15px' }} />
                  </EmtButton>
                </Box>

                <PetsittersSelectionButtons id={customerId} />

                {petsitterPrice?.cancellationPolicies && (
                  <EmtTypography variant="caption" color="greyGradient.light" fontWeight={400} textAlign="center">
                    <b>Annulation gratuite</b>{' '}
                    {(
                      cancellationPoliciesMapping.find((item) => item.slug === petsitterPrice?.cancellationPolicies)
                        ?.title || ''
                    ).toLowerCase()}
                    .
                  </EmtTypography>
                )}
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: '12px',
                  padding: '35px 20px 10px',
                }}
              >
                <EmtButton
                  variant="outlined"
                  color="purple"
                  fullWidth
                  sx={{ height: '48px' }}
                  onClick={() => swipeMenuRef.current?.open()}
                >
                  Voir les tarifs
                </EmtButton>
                <PetsittersSelectionButtons id={customerId} compact sx={{ height: '48px', width: '48px' }} />
              </Box>
            )
          }
          isLoading={isLoading}
        >
          {!isLoading && userProfile && (
            <AskSitterCard
              data={{
                ...userProfile,
                reviewsCount: reviewsSummary.reviewsCount,
                reviewsRating: reviewsSummary.averageRating,
              }}
              onScrollToSection={scrollWithOffset}
              shadows={false}
            />
          )}
        </MobileSwipeMenu>
      )}
      {/* END Mobile ask sitter */}
      {/* Similars */}{' '}
      {userProfile?.similarProfiles && userProfile?.similarProfiles.length && (
        <Container maxWidth="lg" sx={{ paddingTop: isMobile ? '0' : '36px', paddingBottom: '36px' }}>
          <Similars data={userProfile.similarProfiles} />
        </Container>
      )}
      {/* END Similars */}
      <Footer />
    </>
  )
}

export default PetSitterProfileComponent
